import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    InputAdornment,
    Link,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { screensApi } from './Screens.api';
import { screenDialogBodySx } from './dialogSx';
import { ScreenSettings, formatBytes } from './Screens.types';

const copy = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
};

const CopyField: React.FC<{ label: string; value: string; secret?: boolean; onCopied: (label: string) => void }> = ({ label, value, secret, onCopied }) => {
    const [visible, setVisible] = useState(!secret);
    return (
        <TextField
            label={label}
            value={visible ? value : '•'.repeat(Math.min(32, value.length || 12))}
            size="small"
            fullWidth
            InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace' },
                endAdornment: (
                    <InputAdornment position="end">
                        {secret && (
                            <Tooltip title={visible ? 'Hide' : 'Reveal'}>
                                <IconButton size="small" onClick={() => setVisible((v) => !v)}>
                                    {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Copy">
                            <IconButton size="small" onClick={() => void copy(value).then((ok) => ok && onCopied(label))}>
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                ),
            }}
        />
    );
};

/**
 * Company ID + screen Secret Key every new screen asks for on first boot, with install instructions
 * and download links for the newest st-ck-screen-client package in Nexus.
 */
export const ScreenCredentialsPanel: React.FC<{
    open: boolean;
    onClose: () => void;
    onMessage: (message: string, severity?: 'success' | 'error') => void;
}> = ({ open, onClose, onMessage }) => {
    const theme = useTheme();
    const [settings, setSettings] = useState<ScreenSettings | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setError(null);
        setLoading(true);
        screensApi
            .settings()
            .then((data) => {
                if (!cancelled) setSettings(data);
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load screen credentials');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    const regenerate = async () => {
        setBusy(true);
        try {
            setSettings(await screensApi.regenerateSecret());
            onMessage('New Secret Key generated. Registered screens keep working.');
        } catch (err) {
            onMessage(err instanceof Error ? err.message : 'Could not regenerate the key', 'error');
        } finally {
            setBusy(false);
            setConfirmOpen(false);
        }
    };

    const latest = settings?.latestRelease;
    const debFor = (arch: string) => latest?.artifacts.find((a) => a.arch === arch);
    const amd64 = debFor('amd64');
    const isoFor = (arch: string) => latest?.installers?.find((a) => a.arch === arch);
    const amd64Iso = isoFor('amd64');
    const arm64Iso = isoFor('arm64');
    // secondary.main is remapped to light grey in the light theme, so links use colours that stay readable.
    const downloadLinkColor = theme.palette.mode === 'light' ? '#1473e6' : '#ffe3a3';
    const commandSx = {
        m: 0,
        p: 1.5,
        borderRadius: 1,
        fontSize: 12,
        overflowX: 'auto',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.04)',
    } as const;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Screen credentials &amp; installation</DialogTitle>
            <DialogContent dividers sx={screenDialogBodySx}>
                {error && <Alert severity="error">{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress color="secondary" />
                    </Box>
                ) : settings && (
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            A new screen asks for these two values on its first start (and after a factory reset). The Secret Key is 5
                            characters: capital letters and digits. Regenerating the key only affects screens that register afterwards.
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 1.5, alignItems: 'center' }}>
                            <CopyField label="Company ID" value={settings.companyId} onCopied={(l) => onMessage(`${l} copied`)} />
                            <CopyField label="Secret Key" value={settings.secretKey} secret onCopied={(l) => onMessage(`${l} copied`)} />
                            <Button variant="outlined" color="warning" startIcon={<AutorenewIcon />} onClick={() => setConfirmOpen(true)} disabled={busy}>
                                Regenerate
                            </Button>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                New machine: flash the installer ISO with balenaEtcher
                            </Typography>
                            {latest?.installers && latest.installers.length > 0 ? (
                                <>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Installer <b>{latest.version}</b>
                                        {latest.installers.map((a) => (
                                            <React.Fragment key={`iso-${a.arch}`}>
                                                {' · '}
                                                <Link href={a.url} target="_blank" rel="noopener" underline="always" sx={{ fontWeight: 700, color: downloadLinkColor }}>
                                                    {a.arch} ISO ({formatBytes(a.sizeBytes)})
                                                </Link>
                                            </React.Fragment>
                                        ))}
                                    </Typography>
                                    <Box component="pre" sx={commandSx}>
                                        {[
                                            '1. Download the ISO that matches the machine:',
                                            ...(amd64Iso ? [`   amd64 (Intel/AMD, BIOS or UEFI)  ${amd64Iso.url}`] : []),
                                            ...(arm64Iso ? [`   arm64 (UEFI only)                ${arm64Iso.url}`] : []),
                                            '2. Open balenaEtcher.',
                                            '3. Flash from file, choose the ISO, choose the USB drive, then Flash.',
                                            '4. Boot the new machine from that USB drive. No network is required.',
                                            '   The installer erases the internal disk, then installs Debian and this screen.',
                                            '5. When the machine reboots, remove the USB drive.',
                                            '   Leaving it plugged in starts the installer again.',
                                            '6. The registration screen appears. Enter the Company ID and Secret Key above.',
                                        ].join('\n')}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                        A machine that is already registered plays its playlist instead of showing registration.
                                    </Typography>
                                </>
                            ) : (
                                <Alert severity="info">No offline installer ISO has been published yet.</Alert>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Existing Debian or Ubuntu machine: install the package only
                            </Typography>
                            {latest ? (
                                <>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Package <b>{latest.version}</b>
                                        {latest.artifacts.map((a) => (
                                            <React.Fragment key={a.arch}>
                                                {' · '}
                                                <Link href={a.url} target="_blank" rel="noopener" underline="always" sx={{ fontWeight: 700, color: downloadLinkColor }}>
                                                    {a.arch} ({formatBytes(a.sizeBytes)})
                                                </Link>
                                            </React.Fragment>
                                        ))}
                                    </Typography>
                                    <Box component="pre" sx={commandSx}>
                                        {[
                                            'export PATH="/usr/local/sbin:/usr/sbin:/sbin:$PATH"',
                                            `wget -O st-ck-screen-client_${latest.version}_amd64.deb \\`,
                                            `  ${amd64?.url ?? '<package-url>'}`,
                                            `dpkg -i st-ck-screen-client_${latest.version}_amd64.deb`,
                                        ].join('\n')}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                        Run this as root on a machine that already has Debian or Ubuntu. A plain <code>su</code> does not put /usr/sbin and /sbin on PATH, so dpkg cannot find ldconfig or start-stop-daemon until that export. The package itself does not need internet.
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        API used by screens: {settings.apiBaseUrl}
                                    </Typography>
                                </>
                            ) : (
                                <Alert severity="info">No st-ck-screen-client package has been published to Nexus yet.</Alert>
                            )}
                        </Box>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" startIcon={<Cancel />} onClick={onClose}>
                    Close
                </Button>
            </DialogActions>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Regenerate the Secret Key?</DialogTitle>
                <DialogContent sx={screenDialogBodySx}>
                    <DialogContentText>
                        Screens that are already registered keep working. New screens will need the new key; the old key stops working
                        immediately.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="secondary" onClick={() => setConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="warning" variant="contained" onClick={() => void regenerate()} disabled={busy}>
                        Regenerate
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default ScreenCredentialsPanel;
